// components/TextSection.tsx
import React, { useState } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import Button from '@/UIlibrary/Button/Button';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import { Info, Copy, Check } from 'react-feather';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import toast from 'react-hot-toast';

interface AnalysisTextSectionProps {
	label: string;
	value: any;
	setDetailedAnswerModalContent?: (value: any) => void;
	setOpenDetailedAnswerModal?: (value: boolean) => void;
	tooltip?: string;
	copyable?: boolean;
}

export const AnalysisTextSection: React.FC<AnalysisTextSectionProps> = ({
	label,
	value,
	setDetailedAnswerModalContent,
	setOpenDetailedAnswerModal,
	tooltip,
	copyable = false
}) => {
	const t = useTranslations('RegistryDetails');

	const CopyButton = ({ text }: { text: string }) => {
		const [copied, setCopied] = useState(false);

		const getTextToCopy = () => {
			if (label === 'MRZ') {
				return text.replace(/</g, '');
			}
			return text;
		};

		const handleCopy = async () => {
			try {
				const textToCopy = getTextToCopy();
				await navigator.clipboard.writeText(textToCopy);
				setCopied(true);
				toast.success(`${t('CopiedToClipboard') || 'Copied to clipboard'}: ${text}`, {
					duration: 3000
				});
				setTimeout(() => setCopied(false), 2000);
			} catch {
				toast.error(t('CopyFailed') || 'Failed to copy');
			}
		};

		const truncateText = (str: string, maxLength: number = 50) => {
			let displayText = str;
			if (label === 'MRZ') {
				displayText = str.replace(/</g, '');
			}
			if (displayText.length <= maxLength) return displayText;
			return `${displayText.substring(0, maxLength)}...`;
		};

		const button = (
			<button
				onClick={handleCopy}
				className="p-1 hover:bg-gray-100 rounded transition-colors"
				aria-label="Copy to clipboard"
			>
				{copied ? (
					<Check size={16} color={BRAND_COLORS.brand500} />
				) : (
					<Copy size={16} color={GRAY_COLORS.gray600} />
				)}
			</button>
		);

		return copied ? (
			<Tooltip content={`${t('CopiedToClipboard') || 'Copied'}: ${truncateText(text)}`}>
				{button}
			</Tooltip>
		) : (
			button
		);
	};

	const isNotEmpty = (value: any) => {
		if (Array.isArray(value)) {
			return value.length > 0;
		}
		if (typeof value === 'object' && value !== null) {
			return Object.keys(value).length > 0;
		}
		return false;
	};

	const displayValue = () => {
		const jsonValues = [
			'Sanctions',
			'PEP',
			'Warnings',
			'OfficialIdFaceWarnings',
			'SelfieFaceWarnings'
		];
		if (value === null || value === undefined || value === '') {
			return (
				<Typography size="md" weight="bold">
					-
				</Typography>
			);
		}

		if (
			label === 'MRZ' &&
			typeof value === 'string' &&
			setDetailedAnswerModalContent &&
			setOpenDetailedAnswerModal
		) {
			const truncatedValue = value.length > 20 ? `${value.substring(0, 20)}...` : value;
			return (
				<div className="flex flex-row gap-2 items-center">
					<CopyButton text={value} />
					<Typography size="md" weight="bold" className="break-words max-w-md">
						{truncatedValue}
					</Typography>
					<Button
						textOnly
						label={t('Detail')}
						onClick={() => {
							setDetailedAnswerModalContent([value]);
							setOpenDetailedAnswerModal(true);
						}}
						color="gray"
					/>
				</div>
			);
		}

		if (jsonValues.includes(label) && setDetailedAnswerModalContent && setOpenDetailedAnswerModal) {
			if (isNotEmpty(value)) {
				return (
					<div className="flex flex-row gap-2">
						<Typography>{t('Yes')}</Typography>
						<Button
							textOnly
							label={t('Detail')}
							onClick={() => {
								setDetailedAnswerModalContent(value);
								setOpenDetailedAnswerModal(true);
							}}
							color="gray"
							disabled={!isNotEmpty(value)}
						/>
					</div>
				);
			} else {
				return <Typography>{t('No')}</Typography>;
			}
		}
		if (typeof value === 'boolean') {
			return (
				<Typography size="md" weight="bold">
					{value ? t('Yes') : t('No')}
				</Typography>
			);
		}
		if (copyable && typeof value === 'string') {
			return (
				<div className="flex flex-row gap-2 items-center">
					<CopyButton text={value} />
					<Typography size="md" weight="bold" className="break-words">
						{value}
					</Typography>
				</div>
			);
		}

		return (
			<Typography size="md" weight="bold" className="break-words">
				{value}
			</Typography>
		);
	};

	const labelElement = (
		<div className="flex items-center gap-1">
			<Typography size="sm" color={GRAY_COLORS.gray600}>
				{t(toTranslationKey(label))}:
			</Typography>
			{tooltip && (
				<Tooltip content={tooltip}>
					<Info color={BRAND_COLORS.brand600} size={14} className="cursor-pointer" />
				</Tooltip>
			)}
		</div>
	);

	return (
		<div className="flex items-center gap-2">
			<div className="flex justify-between items-center w-full">
				{labelElement}
				{displayValue()}
			</div>
		</div>
	);
};

export default AnalysisTextSection;
